import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Star, Trophy, User, LogOut, Settings, Menu, X, Share2, Medal
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const BADGE_COLORS = {
  "Food Novice": "badge-novice",
  "Food Explorer": "badge-explorer",
  "Food Connoisseur": "badge-connoisseur",
  "Prince Foody": "badge-prince",
  "Royal Foody": "badge-royal"
};

const Leaderboard = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard`, { withCredentials: true });
      setLeaderboard(response.data);
    } catch (error) {
      console.warn("Error fetching leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleShare = () => {
    const myRank = leaderboard.find(e => e.user_id === user?.user_id);
    const shareText = myRank 
      ? `I'm ranked #${myRank.rank} on Étoile with ${myRank.total_stars} Michelin stars! 🌟`
      : `Check out Étoile - The Premier Michelin Dining Tracker!`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Étoile - Michelin Star Tracker',
        text: shareText,
        url: window.location.origin
      });
    } else {
      navigator.clipboard.writeText(`${shareText} ${window.location.origin}`);
      toast.success("Link copied to clipboard!");
    }
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return "rank-1 text-2xl";
    if (rank === 2) return "rank-2 text-xl";
    if (rank === 3) return "rank-3 text-xl";
    return "text-muted-foreground";
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-amber-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return null;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="leaderboard-page">
      {/* Navigation */}
      <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <Star className="w-5 h-5 text-primary fill-primary" />
              <span className="font-semibold gradient-text">Étoile</span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => navigate('/dashboard')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">{t('common.dashboard')}</button>
              <button onClick={() => navigate('/restaurants')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">{t('common.restaurants')}</button>
              <button onClick={() => navigate('/preferences')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">{t('common.preferences')}</button>
              <button onClick={() => navigate('/leaderboard')} className="menu-item text-sm text-foreground py-2">{t('common.leaderboard')}</button>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 h-10">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-popover border-border">
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />Profile Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-muted/50" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-border">
              <div className="flex flex-col gap-2">
                <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">{t('common.dashboard')}</button>
                <button onClick={() => { navigate('/restaurants'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">{t('common.restaurants')}</button>
                <button onClick={() => { navigate('/preferences'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">{t('common.preferences')}</button>
                <button onClick={() => { navigate('/leaderboard'); setMobileMenuOpen(false); }} className="text-left py-2 text-foreground">{t('common.leaderboard')}</button>
                <button onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">Profile</button>
                <button onClick={handleLogout} className="text-left py-2 text-destructive">Logout</button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">{t('common.leaderboard')}</h1>
            <p className="text-muted-foreground">The world's top Michelin diners</p>
          </div>
          <Button 
            onClick={handleShare}
            variant="outline"
            className="border-border hover:border-primary hover:text-primary"
            data-testid="share-button"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share Your Rank
          </Button>
        </div>

        {/* Top 3 Podium */}
        {leaderboard.length >= 3 && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            {/* 2nd Place */}
            <div className="order-1 pt-8">
              <Card className="bg-card border-border text-center p-6" data-testid="rank-2-card">
                <div className="flex justify-center mb-3">
                  <Medal className="w-8 h-8 text-gray-400" />
                </div>
                {leaderboard[1]?.picture ? (
                  <img src={leaderboard[1].picture} alt="" className="w-16 h-16 rounded-full mx-auto mb-3" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-muted mx-auto mb-3 flex items-center justify-center">
                    <User className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
                <p className="font-semibold truncate">{leaderboard[1]?.name}</p>
                <p className="text-2xl font-bold text-gray-400">{leaderboard[1]?.total_stars}</p>
                <p className="text-xs text-muted-foreground">stars</p>
              </Card>
            </div>

            {/* 1st Place */}
            <div className="order-2">
              <Card className="bg-card border-primary/30 text-center p-6" data-testid="rank-1-card">
                <div className="flex justify-center mb-3">
                  <Trophy className="w-10 h-10 text-amber-400" />
                </div>
                {leaderboard[0]?.picture ? (
                  <img src={leaderboard[0].picture} alt="" className="w-20 h-20 rounded-full mx-auto mb-3 border-2 border-primary" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-primary/20 mx-auto mb-3 flex items-center justify-center border-2 border-primary">
                    <User className="w-10 h-10 text-primary" />
                  </div>
                )}
                <p className="font-semibold truncate">{leaderboard[0]?.name}</p>
                <p className="text-3xl font-bold gradient-text">{leaderboard[0]?.total_stars}</p>
                <p className="text-xs text-muted-foreground">stars</p>
              </Card>
            </div>

            {/* 3rd Place */}
            <div className="order-3 pt-12">
              <Card className="bg-card border-border text-center p-6" data-testid="rank-3-card">
                <div className="flex justify-center mb-3">
                  <Medal className="w-7 h-7 text-amber-600" />
                </div>
                {leaderboard[2]?.picture ? (
                  <img src={leaderboard[2].picture} alt="" className="w-14 h-14 rounded-full mx-auto mb-3" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-muted mx-auto mb-3 flex items-center justify-center">
                    <User className="w-7 h-7 text-muted-foreground" />
                  </div>
                )}
                <p className="font-semibold truncate text-sm">{leaderboard[2]?.name}</p>
                <p className="text-xl font-bold text-amber-600">{leaderboard[2]?.total_stars}</p>
                <p className="text-xs text-muted-foreground">stars</p>
              </Card>
            </div>
          </div>
        )}

        {/* Full Leaderboard List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : leaderboard.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-12 text-center">
              <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No foodies on the leaderboard yet</p>
              <p className="text-sm text-muted-foreground mt-2">Be the first to track your dining!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {leaderboard.map((entry) => (
              <Card 
                key={entry.user_id}
                className={`bg-card border-border ${entry.user_id === user?.user_id ? 'border-primary/30' : ''}`}
                data-testid={`leaderboard-entry-${entry.rank}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Rank */}
                    <div className={`w-10 text-center font-bold ${getRankStyle(entry.rank)}`}>
                      {getRankIcon(entry.rank) || `#${entry.rank}`}
                    </div>

                    {/* Avatar */}
                    {entry.picture ? (
                      <img src={entry.picture} alt="" className="w-10 h-10 rounded-full" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <User className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}

                    {/* Name & Badge */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold truncate ${entry.user_id === user?.user_id ? 'text-primary' : ''}`}>
                        {entry.name}
                        {entry.user_id === user?.user_id && <span className="text-xs ml-2">(You)</span>}
                      </p>
                      <p className={`text-xs ${BADGE_COLORS[entry.badge_tier]}`}>
                        {entry.badge_tier}
                      </p>
                    </div>

                    {/* Stats */}
                    <div className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <Star className="w-4 h-4 text-primary fill-primary" />
                        <span className="font-bold">{entry.total_stars}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{entry.visit_count} visits</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Leaderboard;
